import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { categoryData } from '@/mocks/categories';
import { diagnosisQuestions } from '@/mocks/diagnosis';

export default function Diagnose() {
  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();
  const cat = category as keyof typeof categoryData;
  const catInfo = categoryData[cat];

  const questions = diagnosisQuestions[cat] || [];
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>();

  const progress = questions.length > 0 ? ((currentStep + 1) / questions.length) * 100 : 0;
  const question = questions[currentStep];

  const selectOption = useCallback(
    (value: string) => {
      if (!question) return;
      setAnswers((prev) => ({ ...prev, [question.id]: value }));
    },
    [question],
  );

  const isAnswered = question ? answers?.[question.id] !== undefined : false;

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      navigate(`/result/${cat}`);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    } else {
      navigate(`/guide/${cat}`);
    }
  };

  if (!catInfo || !question) {
    return (
      <div className="min-h-[100dvh] bg-cream flex items-center justify-center">
        <p>존재하지 않는 카테고리입니다</p>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-cream flex flex-col">
      <div className="px-5 pt-6 pb-4 bg-white border-b border-cream-dark shrink-0">
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={handlePrev}
            className="w-8 h-8 flex items-center justify-center text-navy cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <span className="text-beige-dark text-sm">{catInfo.name} 진단</span>
        </div>
        <div className="flex items-center gap-3 mb-3">
          <div className="flex-1 h-1.5 bg-cream-dark rounded-full overflow-hidden">
            <div
              className="h-full bg-navy rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-xs text-beige-dark font-medium">
            {currentStep + 1}/{questions.length}
          </span>
        </div>
        <p className="text-softgold text-xs font-medium">
          Q{question.id}. {question.question}
        </p>
      </div>

      <div className="flex-1 px-5 py-6 overflow-y-auto">
        <h2 className="text-navy text-lg font-bold leading-snug mb-5">{question.question}</h2>

        <div className="grid grid-cols-1 gap-3">
          {question.options.map((opt) => {
            const selected = answers?.[question.id] === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => selectOption(opt.value)}
                className={`p-5 rounded-2xl border-2 text-left transition-all cursor-pointer ${
                  selected
                    ? 'border-navy bg-navy/[0.03]'
                    : 'border-cream-dark bg-white hover:border-beige'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors ${
                      selected ? 'border-navy bg-navy' : 'border-cream-dark'
                    }`}
                  >
                    {selected && <i className="ri-check-line text-white text-xs" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-bold text-sm mb-1.5 ${selected ? 'text-navy' : 'text-gray-800'}`}>
                      {opt.label}
                    </p>
                    <p className="text-gray-500 text-xs leading-relaxed mb-2">{opt.desc}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {opt.tags.map((tag, i) => (
                        <span
                          key={i}
                          className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            selected
                              ? 'bg-navy/10 text-navy'
                              : 'bg-cream text-beige-dark'
                          }`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-5 pb-[env(safe-area-inset-bottom)] pt-3 bg-white border-t border-cream-dark shrink-0">
        <div className="flex gap-3">
          <button
            onClick={handlePrev}
            className="px-5 py-4 rounded-xl border-2 border-cream-dark text-navy font-medium text-sm cursor-pointer hover:bg-cream transition-colors whitespace-nowrap"
          >
            이전
          </button>
          <button
            onClick={handleNext}
            disabled={!isAnswered}
            className={`flex-1 py-4 rounded-xl font-semibold text-base transition-all cursor-pointer whitespace-nowrap ${
              isAnswered
                ? 'bg-navy text-white hover:bg-navy-light'
                : 'bg-cream-dark text-beige-dark cursor-not-allowed'
            }`}
          >
            {currentStep < questions.length - 1 ? '다음' : '결과 확인하기'}
          </button>
        </div>
      </div>
    </div>
  );
}